import { Flex, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatMonthDate } from '../../../../../../helpers/date-time-helpers';
import { useWindowSize } from '../../../../../../hooks/useWindowSize';
import { HEIGHT_BREAKPOINTS } from '../../../../../../styles/breakpoints';
import { Text } from '../../../../../../ui-library/typography/typography';
import { Metric } from '../../../../strategy.type';

interface FlowByPhaseTooltipEntry {
  dataKey?: string | number;
  name?: string;
  value?: number;
}

interface FlowByPhaseChartTooltipProps {
  /** Injected by recharts when the pointer is over the chart. */
  active?: boolean;
  /** Injected by recharts: the x-axis value under the pointer. */
  label?: string;
  /** Injected by recharts: one entry per rendered series. */
  payload?: FlowByPhaseTooltipEntry[];
  /** Rows to render, in the order they should appear (top band first). */
  phaseMetadata?: Array<{ id: string; label: string; color: string }>;
  /**
   * Selects the `tasks_*` or `points_*` series. Required: rows are resolved by
   * reconstructing the dataKey from it, so a wrong or missing value silently
   * renders every row as `0` rather than failing.
   */
  metric: Metric;
}

/**
 * Custom Flow-by-Phase tooltip: one row per phase, in the order `phaseMetadata`
 * supplies, with each row's value looked up from the recharts payload by
 * dataKey.
 *
 * @param {FlowByPhaseChartTooltipProps} props - Recharts-injected tooltip state
 *   plus the phase rows and metric to render.
 * @returns {JSX.Element | null} The tooltip card, or null when inactive
 */
export const FlowByPhaseChartTooltip = ({
  active,
  payload,
  label,
  phaseMetadata,
  metric,
}: FlowByPhaseChartTooltipProps) => {
  const i18n = useTranslation();
  const { height } = useWindowSize();

  if (!active || !phaseMetadata) {
    return null;
  }

  const isExpandedTooltip = height > parseInt(HEIGHT_BREAKPOINTS.md, 10);

  return (
    <Stack
      gap={isExpandedTooltip ? 'xs' : 0}
      p={isExpandedTooltip ? 'xs' : '4px 8px'}
      style={{
        backgroundColor: 'white',
        borderRadius: 4,
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: 300,
      }}
    >
      <Text size="small" weight="medium">
        {formatMonthDate(label)}
      </Text>

      <Stack gap={isExpandedTooltip ? 'xs' : 0}>
        {phaseMetadata.map((phase) => {
          // Match on dataKey, not on the display label: sub-group labels are
          // resolved per board and two source statuses can legitimately render
          // the same text, in which case a label match returns whichever series
          // recharts pushed first and the two rows show the same value.
          const dataKey = metric === Metric.Tasks ? `tasks_${phase.id}` : `points_${phase.id}`;
          const entry = payload?.find((entryCandidate) => entryCandidate.dataKey === dataKey);
          const value = entry?.value !== undefined ? Math.round(entry.value) : 0;

          return (
            <Flex key={phase.id} align="flex-start" justify="space-between" gap={8}>
              <Flex align="flex-start" gap={8} style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: phase.color,
                    flexShrink: 0,
                    marginTop: 6,
                  }}
                />
                <Text size="small" style={{ wordBreak: 'break-word', flex: 1 }}>
                  {phase.label}
                </Text>
              </Flex>
              <Text size="small" weight="medium" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {value}{' '}
                {metric === Metric.Tasks
                  ? i18n.t('common.work_units').toLowerCase()
                  : i18n.t('common.estimation').toLowerCase()}
              </Text>
            </Flex>
          );
        })}
      </Stack>
    </Stack>
  );
};
